import React, { useState, useEffect } from 'react';

const GuestSeatingList = () => {
  const [seatingData, setSeatingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchSeatingData();
  }, []);

  const fetchSeatingData = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/seating-chart/public');
      const data = await response.json();
      
      if (data.success) {
        setSeatingData(data.data);
      } else {
        setError(data.error || '获取宾客名单失败');
      }
    } catch (err) {
      setError('获取宾客名单失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // Filter guests based on search term
  const filteredGuests = seatingData?.all_guests.filter(guest =>
    guest.name.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Sort guests alphabetically
  const sortedGuests = [...filteredGuests].sort((a, b) => 
    a.name.localeCompare(b.name, 'zh-CN')
  );

  // Count assigned vs unassigned
  const assignedCount = sortedGuests.filter(g => g.table_number).length;
  const unassignedCount = sortedGuests.filter(g => !g.table_number).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-xl text-gray-600">加载宾客名单中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-800">{error}</p>
        <button 
          onClick={fetchSeatingData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          重试 Retry
        </button>
      </div>
    );
  }

  if (!seatingData || seatingData.all_guests.length === 0) {
    return (
      <div className="text-center py-12 text-gray-600">
        <div className="text-4xl mb-4">📋</div>
        <p className="text-lg">宾客名单即将公布</p>
        <p className="text-sm mt-2">Guest list coming soon</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="text-center sm:text-left">
          <h3 className="text-2xl font-chinese font-bold text-chinese-dark">
            座位安排 Seating Assignments
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            共 {seatingData.all_guests.length} 位宾客 • {assignedCount} 已分配 • {unassignedCount} 待分配
          </p>
        </div>
        
        {/* Search Box */}
        <div className="w-full sm:w-auto">
          <input
            type="text"
            placeholder="搜索宾客姓名 Search guest..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full sm:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-chinese-red focus:border-transparent"
          />
        </div>
      </div>

      {/* Guest Cards - Desktop: 3 columns, Mobile: 1 column */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sortedGuests.length === 0 ? (
          <div className="col-span-full text-center py-8 text-gray-500">
            <p>没有找到匹配的宾客</p>
            <p className="text-sm mt-1">No guests found matching "{searchTerm}"</p>
          </div>
        ) : (
          sortedGuests.map((guest, idx) => (
            <div
              key={idx}
              className={`p-4 rounded-lg border-2 shadow-sm hover:shadow-md transition-shadow ${
                guest.table_number
                  ? 'bg-blue-50 border-blue-200'
                  : 'bg-yellow-50 border-yellow-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-chinese font-bold text-gray-800 text-lg">
                    {(guest.name || '').replace(/（[^）]*）/g, '').replace(/\([^)]*\)/g, '')}
                  </h4>
                  <div className="text-sm text-gray-600 mt-1">
                    👥 {guest.party_size} 位
                    {guest.party_size > 1 && ' guests'}
                  </div>
                </div>
                <div className="ml-2">
                  {guest.table_number ? (
                    <div className="text-center">
                      <div className="text-2xl">🪑</div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="text-2xl">⏳</div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="mt-3 pt-3 border-t border-gray-200">
                {guest.table_number ? (
                  <div className="text-center">
                    <div className="font-bold text-chinese-red text-lg">
                      餐桌{guest.table_number}
                    </div>
                    {/* 位置信息隐藏，暂不显示 Position info is hidden */}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 text-sm">
                    <div>待分配</div>
                    <div className="text-xs">To Be Assigned</div>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer Note */}
      {sortedGuests.length > 0 && (
        <div className="mt-6 text-center text-sm text-gray-600 bg-gray-50 rounded-lg p-4">
          <p>💡 温馨提示：请记住您的座位号，婚礼当天根据座位号入座</p>
          <p className="text-xs mt-1">
            Tip: Please remember your seat number and find your seat on the wedding day
          </p>
        </div>
      )}
    </div>
  );
};

export default GuestSeatingList;

